<script setup>
import { ref, onMounted, watch, onUnmounted, nextTick, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n'

import { NInput, NButton, NSpin, NAlert, NPagination, NSelect } from "naive-ui";
import ProviderCard from '../components/ProviderCard.vue';
import { fetchProvidersForValidator } from "../services/provider";
import { isAuthenticated, currentUser, authToken } from '../services/auth';
import { dt } from '@/utils/i18n'

const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n()

// Reactive state
const providers = ref([]);
const loading = ref(false);
const error = ref(null);
const searchQuery = ref("");
const selectedLanguage = ref(
  localStorage.getItem("preferredLanguage") || "english"
);

// Validator-specific filters
const selectedSource = ref("agency"); // Default to agency data for validators
const selectedStatus = ref("raw"); // Default to what validators should work on
const selectedCity = ref('')
const selectedState = ref('')

// Pagination state
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

// Map state
const mapContainer = ref(null);
const showMap = ref(true); // Show map by default
let map = null;
let markersGroup = null;

// Debounce timer for auto-search
let searchTimeout = null;

// Filter options
const sourceOptions = [
  { label: 'Agency Data', value: 'agency' },
  { label: 'Provider Created', value: 'provider' },
  { label: 'All Sources', value: null }
];

const statusOptions = [
  { label: 'Raw', value: 'raw' },
  { label: 'Unverified', value: 'unverified' },
  { label: 'Verified', value: 'verified' }
];

const stateOptions = [
  // States (28)
  { label: "Andhra Pradesh", value: "Andhra Pradesh" },
  { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
  { label: "Assam", value: "Assam" },
  { label: "Bihar", value: "Bihar" },
  { label: "Chhattisgarh", value: "Chhattisgarh" },
  { label: "Goa", value: "Goa" },
  { label: "Gujarat", value: "Gujarat" },
  { label: "Haryana", value: "Haryana" },
  { label: "Himachal Pradesh", value: "Himachal Pradesh" },
  { label: "Jharkhand", value: "Jharkhand" },
  { label: "Karnataka", value: "Karnataka" },
  { label: "Kerala", value: "Kerala" },
  { label: "Madhya Pradesh", value: "Madhya Pradesh" },
  { label: "Maharashtra", value: "Maharashtra" },
  { label: "Manipur", value: "Manipur" },
  { label: "Meghalaya", value: "Meghalaya" },
  { label: "Mizoram", value: "Mizoram" },
  { label: "Nagaland", value: "Nagaland" },
  { label: "Odisha", value: "Odisha" },
  { label: "Punjab", value: "Punjab" },
  { label: "Rajasthan", value: "Rajasthan" },
  { label: "Sikkim", value: "Sikkim" },
  { label: "Tamil Nadu", value: "Tamil Nadu" },
  { label: "Telangana", value: "Telangana" },
  { label: "Tripura", value: "Tripura" },
  { label: "Uttar Pradesh", value: "Uttar Pradesh" },
  { label: "Uttarakhand", value: "Uttarakhand" },
  { label: "West Bengal", value: "West Bengal" },
  // Union Territories (8)
  { label: "Andaman and Nicobar Islands", value: "Andaman and Nicobar Islands" },
  { label: "Chandigarh", value: "Chandigarh" },
  { label: "Dadra and Nagar Haveli and Daman and Diu", value: "Dadra and Nagar Haveli and Daman and Diu" },
  { label: "Delhi", value: "Delhi" },
  { label: "Jammu and Kashmir", value: "Jammu and Kashmir" },
  { label: "Ladakh", value: "Ladakh" },
  { label: "Lakshadweep", value: "Lakshadweep" },
  { label: "Puducherry", value: "Puducherry" },
];

// Map utility functions
const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      if (window.L) {
        resolve(window.L);
      } else {
        reject(new Error('Failed to load Leaflet'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.head.appendChild(script);
  });
};

const initializeMap = async () => {
  try {
    console.log('Starting map initialization...');
    const L = await loadLeaflet();
    console.log('Leaflet loaded successfully');
    
    if (!mapContainer.value) {
      console.error('Map container not found');
      return;
    }

    console.log('Map container found:', mapContainer.value);

    // Clear any existing map
    if (map) {
      map.remove();
      map = null;
      markersGroup = null;
    }

    // Initialize the map
    map = L.map(mapContainer.value).setView([20.5937, 78.9629], 5); // Center on India
    console.log('Map instance created');

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    console.log('Tile layer added');

    // Initialize markers group
    markersGroup = L.layerGroup().addTo(map);
    console.log('Markers group created');

    // Add providers if they exist
    if (providers.value.length > 0) {
      console.log('Adding markers for existing providers...');
      addProviderMarkers();
    }

    console.log('Map initialized successfully');
  } catch (error) {
    console.error('Error initializing map:', error);
  }
};

const getProviderIcon = (provider) => {
  const L = window.L;
  if (!L) return null;

  let color;
  switch (provider.verification_status || provider.status) {
    case 'verified':
      color = '#10B981'; // green
      break;
    case 'unverified':
      color = '#F59E0B'; // yellow/orange
      break;
    case 'raw':
      color = '#EF4444'; // red
      break;
    default:
      color = '#6B7280'; // gray
  }

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    className: 'custom-marker',
    iconSize: [12, 12],
    iconAnchor: [6, 6]
  });
};

const addProviderMarkers = () => {
  console.log('addProviderMarkers called');
  
  if (!map || !markersGroup || !window.L) {
    console.log('Map not ready for markers - map:', !!map, 'markersGroup:', !!markersGroup, 'Leaflet:', !!window.L);
    return;
  }

  const L = window.L;
  
  // Clear existing markers
  markersGroup.clearLayers();
  console.log('Cleared existing markers');

  let validProviders = 0;
  const bounds = L.latLngBounds();
  let markersAdded = 0;

  console.log('Processing', providers.value.length, 'providers');

  providers.value.forEach((provider, index) => {
    console.log(`Processing provider ${index + 1}:`, {
      name: provider.provider_name,
      lat: provider.latitude,
      lng: provider.longitude,
      rawLat: provider.latitude,
      rawLng: provider.longitude
    });

    const lat = parseFloat(provider.latitude);
    const lng = parseFloat(provider.longitude);

    console.log(`Parsed coordinates: lat=${lat}, lng=${lng}`);

    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      validProviders++;
      
      try {
        const icon = getProviderIcon(provider);
        const marker = L.marker([lat, lng], { icon });
        markersGroup.addLayer(marker);
        markersAdded++;

        console.log(`Added marker ${markersAdded} for ${provider.provider_name} at [${lat}, ${lng}]`);

        // Get status color for popup
        let statusColor;
        switch (provider.verification_status || provider.status) {
          case 'verified':
            statusColor = '#10B981';
            break;
          case 'unverified':
            statusColor = '#F59E0B';
            break;
          case 'raw':
            statusColor = '#EF4444';
            break;
          default:
            statusColor = '#6B7280';
        }

        const popupContent = `
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${provider.provider_name || provider.name}</h4>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
              <strong>City:</strong> ${provider.city || 'N/A'}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
              <strong>State:</strong> ${provider.state || 'N/A'}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">
              <strong>Type:</strong> ${provider.provider_type || 'N/A'}
            </p>
            <p style="margin: 0; font-size: 12px; color: #666;">
              <strong>Status:</strong> <span style="color: ${statusColor}; font-weight: bold;">${provider.verification_status || provider.status}</span>
            </p>
            <div style="margin-top: 10px;">
              <button onclick="window.location.href='/providers/${provider.id}'" style="
                background: #3B82F6; 
                color: white; 
                border: none; 
                padding: 6px 12px; 
                border-radius: 4px; 
                cursor: pointer; 
                font-size: 12px;
              ">View Details</button>
            </div>
          </div>
        `;

        marker.bindPopup(popupContent);
        bounds.extend([lat, lng]);
      } catch (err) {
        console.error(`Error adding marker for provider ${provider.provider_name}:`, err);
      }
    } else {
      console.log(`Invalid coordinates for provider ${provider.provider_name}: lat=${lat}, lng=${lng}`);
    }
  });

  // Fit map to show all markers
  if (validProviders > 0 && bounds.isValid()) {
    console.log('Fitting map bounds to show all markers');
    map.fitBounds(bounds, { padding: [20, 20] });
  } else if (validProviders > 0) {
    console.log('Valid providers found but bounds invalid');
  } else {
    console.log('No valid providers found for mapping');
  }

  console.log(`Added ${markersAdded} markers to map (${validProviders} valid providers total)`);
};

const toggleMap = () => {
  showMap.value = !showMap.value;
  
  if (showMap.value) {
    nextTick(() => {
      initializeMap();
    });
  }
};

// Debounced search for text inputs
const debouncedSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    searchProviders();
  }, 500); // Wait 500ms after user stops typing
};

// Clear filters function
const clearFilters = () => {
  selectedSource.value = "agency";
  selectedStatus.value = "raw";
  selectedCity.value = '';
  selectedState.value = '';
  searchQuery.value = '';
  searchProviders();
};

// Initialize search query from URL parameters if they exist
onMounted(async () => {
  console.log('Component mounted');
  
  if (route.query.q) {
    searchQuery.value = route.query.q;
  }

  if (route.query.language) {
    selectedLanguage.value = route.query.language;
  }

  if (route.query.source) {
    selectedSource.value = route.query.source;
  }

  if (route.query.status) {
    selectedStatus.value = route.query.status;
  }

  if (route.query.city) {
    selectedCity.value = route.query.city;
  }

  if (route.query.state) {
    selectedState.value = route.query.state;
  }

  if (route.query.page) {
    currentPage.value = parseInt(route.query.page) || 1;
  }

  // Perform search with URL parameters
  await loadProviders();
  
  console.log('Initializing map after providers loaded...');
  // Initialize map after DOM is ready and providers are loaded
  await nextTick();
  if (showMap.value) {
    await initializeMap();
  }
  
  // Add event listener for the app-language-updated event
  window.addEventListener('language-changed', handleLanguageChange);
  window.addEventListener('app-language-updated', handleAppLanguageUpdate);
});

// Clean up event listeners when component is unmounted
onUnmounted(() => {
  window.removeEventListener('language-changed', handleLanguageChange);
  window.removeEventListener('app-language-updated', handleAppLanguageUpdate);
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  if (map) {
    map.remove();
    map = null;
  }
});

// Handler for language-changed event
const handleLanguageChange = (event) => {
  const newLanguage = event.detail.language || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;
    currentPage.value = 1;
    searchProviders();
  }
};

// Handler for app-language-updated event (backup)
const handleAppLanguageUpdate = (event) => {
  const newLanguage = localStorage.getItem("preferredLanguage") || "english";
  if (selectedLanguage.value !== newLanguage) {
    selectedLanguage.value = newLanguage;
    currentPage.value = 1;
    searchProviders();
  }
};

// Check if user is validator
const isValidator = () => {
  return currentUser.value?.role === 'validator' || currentUser.value?.role === 'admin';
};

// Check if provider can be edited by validator
const canEditProvider = (provider) => {
  const canEdit = provider.source === 'agency' && provider.status === 'raw';
  console.log('Provider ID:', provider.id, 'Source:', provider.source, 'Status:', provider.status, 'Can Edit:', canEdit);
  return canEdit;
};

// Navigate to provider edit page
const editProvider = (provider) => {
  console.log('Navigating to edit provider:', provider.id);
  router.push(`/provider-edit/${provider.id}`);
};

// Refresh providers list when returning from edit page
const onProviderSaved = () => {
  loadProviders();
};

// Load providers using validator service
const loadProviders = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await fetchProvidersForValidator(
      authToken.value,
      searchQuery.value,
      selectedLanguage.value,
      currentPage.value,
      pageSize.value,
      selectedSource.value === null ? null : selectedSource.value,
      selectedStatus.value ? selectedStatus.value : null,
      selectedCity.value || null,    
      selectedState.value || null    
    );
    
    providers.value = response.providers;
    console.log('Providers loaded:', providers.value.length);
    console.log('Sample provider coordinates:', providers.value.slice(0, 3).map(p => ({
      name: p.provider_name,
      lat: p.latitude,
      lng: p.longitude
    })));
    
    // Update pagination data
    totalItems.value = response.pagination.total;
    totalPages.value = response.pagination.total_pages;
    currentPage.value = response.pagination.page;
    pageSize.value = response.pagination.page_size;

    // Update map markers after loading providers
    if (map && markersGroup) {
      console.log('Updating map markers after provider load');
      addProviderMarkers();
    }
    
  } catch (err) {
    console.error("Error fetching providers for validator:", err);
    error.value = `Failed to load providers: ${err.message}`;
    providers.value = [];
  } finally {
    loading.value = false;
  }
};

// Handle page change
const handlePageChange = (page) => {
  currentPage.value = page;
  
  // Update URL with new page
  const query = { ...route.query, page };
  router.replace({ query });
  
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  // Load providers for the new page
  loadProviders();
};

// Search function that updates URL
const searchProviders = async () => {
  // Reset to page 1 when searching
  currentPage.value = 1;

  // Update URL with search parameters
  const query = {};
  if (searchQuery.value) query.q = searchQuery.value;

  query.language = selectedLanguage.value;
  if (selectedSource.value !== null) query.source = selectedSource.value;
  if (selectedStatus.value) query.status = selectedStatus.value;
  if (selectedCity.value) query.city = selectedCity.value;
  if (selectedState.value) query.state = selectedState.value;
  query.page = currentPage.value;

  // Replace current URL with search parameters
  router.replace({ query });

  // Load providers with current search parameters
  await loadProviders();
};

// Watch for route query changes
watch(
  () => route.query,
  (newQuery) => {
    let shouldReload = false;

    if (newQuery.q !== undefined && newQuery.q !== searchQuery.value) {
      searchQuery.value = newQuery.q || "";
      shouldReload = true;
    }

    if (newQuery.language !== undefined && newQuery.language !== selectedLanguage.value) {
      selectedLanguage.value = newQuery.language || "english";
      shouldReload = true;
    }

    if (newQuery.source !== undefined && newQuery.source !== selectedSource.value) {
      selectedSource.value = newQuery.source || "agency";
      shouldReload = true;
    }

    if (newQuery.status !== undefined && newQuery.status !== selectedStatus.value) {
      selectedStatus.value = newQuery.status || "raw";
      shouldReload = true;
    }

    if (newQuery.city !== undefined && newQuery.city !== selectedCity.value) {
      selectedCity.value = newQuery.city || "";
      shouldReload = true;
    }

    if (newQuery.state !== undefined && newQuery.state !== selectedState.value) {
      selectedState.value = newQuery.state || "";
      shouldReload = true;
    }

    if (newQuery.page !== undefined) {
      const newPage = parseInt(newQuery.page) || 1;
      if (newPage !== currentPage.value) {
        currentPage.value = newPage;
        shouldReload = true;
      }
    }

    if (shouldReload) {
      loadProviders();
    }
  }
);

// Watch for route changes to refresh data when coming back from edit page
watch(
  () => route.path,
  (newPath, oldPath) => {
    // If we're coming back to this page from the edit page, refresh the data
    if (newPath === '/validator-search' && oldPath && oldPath.includes('/provider-edit')) {
      console.log('Returning from provider edit, refreshing providers...');
      loadProviders();
    }
  }
);

// Watch providers to update map markers
watch(providers, () => {
  console.log('Providers changed, updating map markers');
  if (map && markersGroup && providers.value.length > 0) {
    addProviderMarkers();
  }
}, { deep: true });
</script>

<template>
  <div class="validator-search-page">
    <!-- Main Content -->
    <main class="main-content">
      <div class="container">
        <!-- Page Header -->
        <div class="page-header">
          <h1 class="page-title">{{ dt('validators.pageTitle') }}</h1>
          <p class="page-subtitle">
            {{ dt('validators.pageSubtitle') }}
          </p>
        </div>

        <!-- Search Section -->
        <div class="search-filter-section">
          <div class="search-container">
            <NInput
              v-model:value="searchQuery"
              :placeholder="dt('validators.searchPlaceholder')" 
              class="search-input"
              size="large"
              @keyup.enter="searchProviders"
              @input="debouncedSearch"
            />
          </div>
          
          <!-- Validator Filters -->
          <div class="filter-container">
            <div class="filter-grid">
              <div class="filter-item">
                <label class="filter-label">{{ dt('validators.sourceLabel') }}:</label>
                <NSelect
                  v-model:value="selectedSource"
                  :options="sourceOptions"
                  :placeholder="dt('validators.selectSource')"
                  @update:value="searchProviders"
                  class="filter-select"
                />
              </div>
              
              <div class="filter-item">
                <label class="filter-label">{{ dt('validators.statusLabel') }}:</label>
                <NSelect
                  v-model:value="selectedStatus"
                  :options="statusOptions"
                  :placeholder="dt('validators.selectStatus')"
                  @update:value="searchProviders"
                  class="filter-select"
                />
              </div>
              
              <div class="filter-item">
                <label class="filter-label">{{ dt('validators.cityLabel') }}:</label>
                <NInput
                  v-model:value="selectedCity"
                  :placeholder="dt('validators.enterCity')"
                  @keyup.enter="searchProviders"
                  @input="debouncedSearch"
                  class="filter-input"
                />
              </div>
              
              <div class="filter-item">
                <label class="filter-label">{{ dt('validators.stateLabel') }}:</label>
                <NSelect
                  v-model:value="selectedState"
                  :options="stateOptions"
                  :placeholder="dt('validators.selectState')"
                  @update:value="searchProviders"
                  class="filter-select"
                  filterable
                  clearable
                />
              </div>
            </div>

            <!-- Filter Actions with Search Button -->
            <div class="filter-actions">
              <NButton
                size="small"
                type="default"
                @click="clearFilters"
                class="clear-button"
              >
                ✕ {{ dt('validators.clearFilters') }}
              </NButton>
              <NButton
                type="primary"
                size="small"
                @click="searchProviders"
                :loading="loading"
                class="search-button-small"
              >
                🔍 {{ dt('validators.searchNow') }}
              </NButton>
              <NButton
                size="small"
                type="default"
                @click="toggleMap"
                class="toggle-map-button"
              >
                {{ showMap ? '🗺️ Hide Map' : '🗺️ Show Map' }}
              </NButton>
            </div>
          </div>
        </div>

        <!-- Main Content Grid: List + Map -->
        <div class="content-grid" :class="{ 'full-width': !showMap }">
          <!-- Providers List on the Left -->
          <div class="list-column">
            <!-- Results Summary -->
            <div v-if="!loading && providers.length > 0" class="results-summary">
              <p>
                {{ dt('validators.showingResults', {
                  start: (currentPage - 1) * pageSize + 1,
                  end: Math.min(currentPage * pageSize, totalItems),
                  total: totalItems
                }) }}
              </p>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="loading-section">
              <NSpin size="large">
                <div class="loading-text">{{ dt('validators.loadingProviders') }}</div>
              </NSpin>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-section">
              <NAlert type="error" :title="dt('validators.error')" closable>
                {{ error }}
                <template #action>
                  <NButton size="small" @click="loadProviders">
                    {{ dt('validators.tryAgain') }}
                  </NButton>
                </template>
              </NAlert>
            </div>

            <!-- Providers List -->
            <div v-else-if="providers.length > 0" class="providers-grid">
              <ProviderCard
                v-for="provider in providers"
                :key="provider.id"
                :provider="provider"
                :showEditButton="canEditProvider(provider)"
                :showBookmarkButton="false"
                @edit="editProvider"
              />
            </div>

            <!-- Empty State -->
            <div v-else class="providers-content">
              <div class="empty-state">
                <div class="empty-icon">👨‍⚕️</div>
                <h3>{{ dt('validators.noProvidersFound') }}</h3>
                <p v-if="searchQuery">
                  {{ dt('validators.noProvidersMatch', { query: searchQuery }) }}
                </p>
                <p v-else>
                  {{ dt('validators.noProvidersAvailable') }}
                </p>
                <div class="empty-actions">
                  <NButton
                    v-if="searchQuery || selectedCity || selectedState"
                    type="default"
                    size="large"
                    style="margin-top: 1rem"
                    @click="clearFilters"
                  >
                    {{ dt('validators.clearFilters') }}
                  </NButton>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="!loading && providers.length > 0 && totalPages > 1" class="pagination-section">
              <NPagination
                v-model:page="currentPage"
                :page-count="totalPages"
                :page-size="pageSize"
                :item-count="totalItems"
                show-size-picker
                :page-sizes="[10, 20, 50, 100]"
                @update:page="handlePageChange"
                @update:page-size="(size) => {
                  pageSize = size;
                  currentPage = 1;
                  searchProviders();
                }"
              >
                <template #prefix="{ itemCount }">
                  {{ dt('validators.providersForReview', { count: itemCount }) }}
                </template>
              </NPagination>
            </div>
          </div>

          <!-- Map on the Right -->
          <div v-if="showMap" class="map-column">
            <div class="map-container">
              <div class="map-header">
                <h3>{{ dt('validators.mapTitle') }}</h3>
                <div class="map-legend">
                  <div class="legend-item">
                    <div class="legend-dot verified"></div>
                    <span>{{ dt('validators.verified') }}</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-dot unverified"></div>
                    <span>{{ dt('validators.unverified') }}</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-dot raw"></div>
                    <span>Raw</span>
                  </div>
                </div>
              </div>
              <div ref="mapContainer" class="map"></div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.validator-search-page {
  min-height: 100vh;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
}

.provider-link {
  color: #3b82f6;
  text-decoration: none;
  transition: color 0.2s;
}

.provider-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-size: 2rem;
  font-weight: bold;
  color: white;
}

.nav {
  display: flex;
  gap: 2rem;
}

.nav-link {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.nav-link:hover {
  color: #f0f0f0;
  background: rgba(255, 255, 255, 0.1);
}

.nav-link.active {
  background: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

.main-content {
  padding: 3rem 0;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #666;
}

.search-filter-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.search-container {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
}

.filter-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.filter-select,
.filter-input {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
}

.clear-button {
  font-size: 0.75rem;
}

.search-button-small {
  font-size: 0.75rem;
}

.toggle-map-button {
  font-size: 0.75rem;
}

/* Content Grid Layout */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  align-items: start;
}

.content-grid.full-width {
  grid-template-columns: 1fr;
}

.list-column {
  min-height: 400px;
}

.map-column {
  position: sticky;
  top: 20px;
}

.map-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.map-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.map-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.map-legend {
  display: flex;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #6b7280;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.legend-dot.verified {
  background-color: #10B981;
}

.legend-dot.unverified {
  background-color: #F59E0B;
}

.legend-dot.raw {
  background-color: #EF4444;
}

.map {
  flex: 1;
  min-height: 0;
}

/* Custom marker styles */
:global(.custom-marker) {
  background: transparent !important;
  border: none !important;
}

/* Responsive adjustments for map */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .map-column {
    position: static;
  }

  .map-container {
    height: 400px;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-filter-section {
    padding: 16px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .filter-actions {
    justify-content: center;
    flex-direction: column;
    gap: 8px;
  }

  .map-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .map-legend {
    justify-content: center;
  }

  .map-container {
    height: 300px;
  }
}

@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .map-header .map-legend {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
  
  .search-filter-section {
    padding: 12px;
  }

  .filter-actions {
    flex-direction: column;
  }
}

.results-summary {
  text-align: center;
  margin-bottom: 2rem;
  color: #666;
  font-size: 0.95rem;
}

.results-summary p {
  margin: 0;
}

.loading-section {
  text-align: center;
  padding: 4rem 0;
}

.loading-text {
  margin-top: 1rem;
  color: #666;
  font-size: 1.1rem;
}

.error-section {
  margin-bottom: 2rem;
}

.providers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.provider-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.provider-card:hover {
  transform: translateY(-2px);
}

.provider-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.provider-header h3 {
  margin: 0;
  color: #333;
}

.provider-id {
  color: #666;
  font-size: 0.9rem;
  background: #f0f0f0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.provider-info p {
  margin: 0.5rem 0;
  color: #555;
}

.provider-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.providers-content {
  background: white;
  border-radius: 10px;
  padding: 3rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  color: #666;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.empty-state p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}

.empty-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  margin-top: 2rem;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 768px) {
  .header .container {
    flex-direction: column;
    gap: 1rem;
  }

  .nav {
    gap: 1rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .action-section {
    flex-direction: column;
    align-items: center;
  }

  .search-container {
    flex-direction: column;
    max-width: 100%;
  }

  .filter-row {
    flex-direction: column;
    gap: 1rem;
  }

  .providers-grid {
    grid-template-columns: 1fr;
  }

  .provider-actions {
    justify-content: center;
  }

  .empty-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-section {
    padding: 1rem 0;
  }
}
</style>